"use client";

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Check, Copy } from "lucide-react";
import { cn } from "@/lib/cn";

// Phase 3.1 — the truncate/reveal/copy treatment for technical values in a
// table cell: hashes, full URLs, user agents, command lines, DNs, JA3s,
// base64 blobs. These are the cells that make a SOC table unusable, because
// each one is both (a) far too long to show in a column and (b) the exact
// string an analyst needs to paste into another tool. Today they either blow
// the column width out or get clipped with no way to see or take the rest.
//
// Three behaviours, all on one element:
//   truncate  one line, ellipsis, the column keeps its width
//   reveal    the full value on hover or keyboard focus
//   copy      one click puts the value on the clipboard
//
// ── Why the reveal is position:fixed and not the DS <Tooltip> ────────────
// .rc-datatable-wrap is `overflow: auto` (it has to be — these tables are
// genuinely wider than the shell). DS Tooltip positions itself with plain
// CSS inside its trigger's containing block, so inside a table it gets
// clipped by that scrollport and the reveal is unreadable exactly where it
// matters most. Fixing Tooltip to escape scroll containers is its own job
// and is scheduled as Phase 4.4; this component cannot wait for it and must
// not ship a reveal that the table eats. A fixed-position layer measured off
// the trigger's own rect sidesteps the clipping entirely and costs one
// getBoundingClientRect per hover.
//
// The reveal is aria-hidden on purpose: truncation here is `text-overflow`,
// so the FULL value is already in the DOM and already announced. The overlay
// is a sighted-user affordance, and marking it up as a tooltip would make a
// screen reader read the same string twice.

export interface MonoCellProps {
  /** The technical value. Also exactly what gets copied. */
  value: string;
  /** Optional display form (e.g. a shortened hash). `value` is still what copies. */
  children?: ReactNode;
  /** Max inline size of the truncated cell. Defaults to the column's width. */
  width?: number | string;
  /** Hide the copy affordance for values nobody pastes anywhere (short ids). */
  copyable?: boolean;
  className?: string;
}

export function MonoCell({ value, children, width, copyable = true, className }: MonoCellProps) {
  const textRef = useRef<HTMLSpanElement | null>(null);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [reveal, setReveal] = useState<{ x: number; y: number } | null>(null);
  const [copied, setCopied] = useState(false);
  // d2d-XDR eval F18: the text was a tab stop in every cell, truncated or
  // not, so a 50-row table cost 50 extra Tab presses per column. It is
  // focusable only while something is actually cut off — the one case where
  // keyboard focus has a job (opening the reveal).
  const [truncated, setTruncated] = useState(false);

  useEffect(() => {
    const el = textRef.current;
    if (!el) return;
    const measure = () => setTruncated(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [value, children]);

  useEffect(() => () => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
  }, []);

  // Only worth revealing when something is actually cut off — a reveal that
  // repeats a fully-visible value is noise on every single row.
  const openReveal = useCallback(() => {
    const el = textRef.current;
    if (!el) return;
    if (el.scrollWidth <= el.clientWidth + 1) return;
    const r = el.getBoundingClientRect();
    setReveal({ x: r.left, y: r.bottom + 4 });
  }, []);

  const closeReveal = useCallback(() => setReveal(null), []);

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 1400);
    } catch {
      // Clipboard is permission-gated and unavailable on insecure origins.
      // Selecting the text is the honest fallback; failing silently would
      // leave the analyst thinking they had copied something.
      const el = textRef.current;
      if (el) {
        const range = document.createRange();
        range.selectNodeContents(el);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      }
    }
  }, [value]);

  // A scroll anywhere moves the trigger out from under a fixed overlay, so
  // the overlay has to go with it. Capture phase catches the table's own
  // scrollport, not just the page.
  useEffect(() => {
    if (!reveal) return;
    const onScroll = () => setReveal(null);
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, [reveal]);

  return (
    <span className={cn("rc-monocell", className)} style={width ? { maxWidth: width } : undefined}>
      <span
        ref={textRef}
        className="rc-monocell__text"
        onMouseEnter={openReveal}
        onMouseLeave={closeReveal}
        onFocus={openReveal}
        onBlur={closeReveal}
        tabIndex={truncated ? 0 : undefined}
      >
        {children ?? value}
      </span>
      {copyable ? (
        <button
          type="button"
          className="rc-monocell__copy"
          aria-label={copied ? `Copied ${value}` : `Copy ${value}`}
          onClick={copy}
        >
          {copied ? <Check aria-hidden /> : <Copy aria-hidden />}
        </button>
      ) : null}
      {/* The copy button's label changes, but a label change is not
          announced; this polite region is (eval F18). */}
      <span className="sr-only" role="status" aria-live="polite">
        {copied ? `Copied ${value}` : ""}
      </span>
      {reveal ? (
        <span className="rc-monocell__reveal" style={{ left: reveal.x, top: reveal.y }} aria-hidden>
          {value}
        </span>
      ) : null}
    </span>
  );
}
